import React, { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import moment from "moment"

import { apiClients } from "~/routes"
import { useToast } from "~/utils/Toast"

import ResourceNotes from "~/library/ResourceNotes"
import CatchCard from "~/library/CatchCard"
import CatchTable from "~/library/CatchTable"
import PageTitle from "~/library/PageTitle"
import { ArrowLeft } from "lucide-react"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchDropdown from "~/library/CatchDropdown"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"

import UpdateClientModal from "./UpdateClientModal"
import { getClientsBackPath, getClientsBackTitle } from "~/utils/navigationState"
import NavigationLink from "~/library/NavigationLink"
import Client from "~/types/Client"
import { formatCurrencyCents } from "~/utils/helpers"

const ClientView = () => {
  const { venueSlug } = getAppConfig()
  const toast = useToast()
  const { id } = useParams()
  const navigate = useNavigate()
  const endpoint = apiClients.show.path({ venueSlug, id })

  const {
    data: client,
    isLoading,
    refetch,
  } = useFetchResource<{ client: Client }, "client">(endpoint, "client", {})

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)

  const updateClient = (values) => {
    const req = apiClients.update({ data: { venueSlug, id, client: values } })
    req.then(() => {
      toast("Client Updated", "success")
      refetch()
    })
    req.catch((res) => {
      toast(`Error updating Booking. ${res.body.errors.join(", ")}`, "danger")
    })
  }

  const renderDetail = (title, content, size = 6) => {
    return (
      <div className={`col-${size || 6}`}>
        <p className="mt-2 mb-1 text-muted fw-bold font-12 text-uppercase">{title}</p>
        <div className="d-flex">
          <div className="mt-1 font-14 mb-2">{content}</div>
        </div>
      </div>
    )
  }

  return (
    <LoadingWrapper isLoading={isLoading}>
      <PageTitle
        title={
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="btn btn-link p-0 me-2"
              onClick={() => navigate(getClientsBackPath())}
              aria-label={`Go back to ${getClientsBackTitle()}`}
              title={`Go back to ${getClientsBackTitle()}`}
            >
              <ArrowLeft />
            </button>
            <span>Client Details</span>
          </div>
        }
      >
        <CatchDropdown
          label="Actions"
          className="ms-2"
          items={[
            {
              label: "Edit Client",
              onClick: () => setIsUpdateModalOpen(true),
            },
          ]}
        />
      </PageTitle>
      <div className="row">
        <div className="col-8">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title mb-2">{client.name}</h4>
              <div className="row">
                {renderDetail("Email", client.email)}
                {renderDetail("Phone", client.phone)}
              </div>
            </div>
          </div>
          <div className="card mt-4">
            <div className="card-body">
              <h4 className="card-title mb-2">Bookings</h4>
              <CatchTable
                data={client.bookings}
                emptyMessage="No Bookings"
                columns={[
                  {
                    key: "name",
                    header: "Booking",
                    formatter: (name, data) => (
                      <NavigationLink type="bookings" to={`/bookings/${data.id}`}>
                        {name}
                      </NavigationLink>
                    ),
                  },
                  {
                    key: "date",
                    header: "Date",
                    formatter: (date) => moment(date).format("MMM Do YYYY"),
                  },
                  {
                    key: "area",
                    header: "Area",
                    formatter: (area) => (
                      <NavigationLink type="areas" to={`/areas/${area.id}`}>
                        {area.name}
                      </NavigationLink>
                    ),
                  },
                  {
                    key: "anticipatedRevenueCents",
                    header: "Anticipated Revenue",
                    formatter: (antRev) => formatCurrencyCents(antRev),
                  },
                ]}
              />
            </div>
          </div>
        </div>
        <div className="col-4">
          <CatchCard title="Notes">
            <ResourceNotes resourceType="client" resourceId={client?.id} />
          </CatchCard>
        </div>
      </div>
      <UpdateClientModal
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        onSubmit={updateClient}
        client={client}
      />
    </LoadingWrapper>
  )
}

export default ClientView
